import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import './HardGate.css';

interface ExamFeedback {
  mistakeCount: number;
  scorePercent: number | null;
  weakTopics: string[];
  examDate: number | null;
}

function readExamFeedback(): ExamFeedback {
  let mistakeCount = 0;
  let scorePercent: number | null = null;
  let weakTopics: string[] = [];
  let examDate: number | null = null;

  try {
    const raw = localStorage.getItem('passFirstExamProgress');
    if (raw) {
      const progress = JSON.parse(raw);
      if (typeof progress.lastExamScore === 'number') {
        scorePercent = Math.round(progress.lastExamScore);
        mistakeCount = Math.round(((100 - progress.lastExamScore) / 100) * 25);
      }
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('aiExamWeakAreas');
    if (raw) {
      const data = JSON.parse(raw);
      if (Array.isArray(data.weakAreas)) {
        weakTopics = (data.weakAreas as Array<{ topic?: string; name?: string }>)
          .slice(0, 3)
          .map((a) => a.topic || a.name || '')
          .filter(Boolean);
      }
    }
  } catch { /* ignore */ }

  try {
    const raw = localStorage.getItem('deferredWeakAreas');
    if (raw && weakTopics.length === 0) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) {
        weakTopics = (arr as Array<string | { topic?: string; name?: string }>)
          .slice(0, 3)
          .map((a) => (typeof a === 'string' ? a : a.topic || a.name || ''))
          .filter(Boolean);
      }
    }
  } catch { /* ignore */ }

  try {
    const stored = localStorage.getItem('userExamDate');
    if (stored) {
      const d = new Date(stored).getTime();
      if (!Number.isNaN(d)) examDate = d;
    }
  } catch { /* ignore */ }

  return { mistakeCount, scorePercent, weakTopics, examDate };
}

const TITLE_ID = 'hard-gate-title';

const TOPIC_LABELS: Record<string, string> = {
  priority: 'Voorrang',
  voorrang: 'Voorrang',
  hazard: 'Gevaarherkenning',
  gevaarherkenning: 'Gevaarherkenning',
  parking: 'Parkeren',
  parkeren: 'Parkeren',
  signs: 'Verkeersborden',
  verkeersborden: 'Verkeersborden',
  roundabout: 'Rotonde',
  rotonde: 'Rotonde',
  overtaking: 'Inhalen',
  inhalen: 'Inhalen',
  speed: 'Snelheid',
  snelheid: 'Snelheid',
  alcohol: 'Alcohol & drugs',
  fatigue: 'Vermoeidheid',
  environment: 'Milieu',
  emergency: 'Pech & nood',
};

function labelTopic(raw: string): string {
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  return TOPIC_LABELS[key] || raw;
}

export const HardGate: React.FC = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();
  const lang = currentLanguage === 'nl' ? 'nl' : currentLanguage === 'ar' ? 'ar' : 'en';

  const feedback = useMemo(() => readExamFeedback(), []);
  const daysLeft = feedback.examDate
    ? Math.ceil((feedback.examDate - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  const showUrgency = daysLeft != null && daysLeft > 0 && daysLeft <= 30;

  const handleContinue = () => navigate('/subscription');

  if (lang === 'ar') {
    return (
      <div
        className="hard-gate-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        dir="rtl"
      >
        <div className="hard-gate-card">
          <h1 id={TITLE_ID} className="hard-gate-title">أنت لست مستعداً بعد</h1>
          <p className="hard-gate-subtitle">لا تزال ترتكب أخطاء في موضوعات مهمة.</p>
          <button type="button" className="hard-gate-cta hard-gate-cta--primary" onClick={handleContinue}>
            واصل التدريب
          </button>
          <p className="hard-gate-hint">يمكنك الإلغاء في أي وقت · أرخص من إعادة الاختبار</p>
        </div>
      </div>
    );
  }

  if (lang === 'en') {
    return (
      <div
        className="hard-gate-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        dir="ltr"
      >
        <div className="hard-gate-card">
          <h1 id={TITLE_ID} className="hard-gate-title">You're not ready yet</h1>
          <p className="hard-gate-subtitle">You're still making mistakes on important topics.</p>
          <button type="button" className="hard-gate-cta hard-gate-cta--primary" onClick={handleContinue}>
            Continue practicing
          </button>
          <p className="hard-gate-hint">Cancel anytime · Cheaper than a re-exam</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="hard-gate-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      dir="ltr"
    >
      <div className="hard-gate-card">

        {/* ── Headline ── */}
        <h1 id={TITLE_ID} className="hard-gate-title">
          Je bent er nog niet klaar voor
        </h1>
        <p className="hard-gate-subtitle">
          Je maakt nog fouten op belangrijke onderwerpen. Dit komt vaak terug op het CBR-examen.
        </p>

        {/* ── Personal feedback block ── */}
        <div className="hard-gate-feedback">
          <div className="hard-gate-feedback__status">
            <span className="hard-gate-feedback__dot" aria-hidden="true" />
            <span className="hard-gate-feedback__label">Nog niet examenklaar</span>
          </div>
          {feedback.mistakeCount > 0 && (
            <div className="hard-gate-feedback__stat">
              <span className="hard-gate-feedback__num">{feedback.mistakeCount}</span>
              <span className="hard-gate-feedback__stat-label">fouten gemaakt</span>
            </div>
          )}
          {feedback.weakTopics.length > 0 && (
            <div className="hard-gate-feedback__topics">
              <p className="hard-gate-feedback__topics-label">Zwakke punten:</p>
              <div className="hard-gate-feedback__chips">
                {feedback.weakTopics.map((t) => (
                  <span key={t} className="hard-gate-feedback__chip">
                    {labelTopic(t)}
                  </span>
                ))}
              </div>
            </div>
          )}
          {feedback.weakTopics.length === 0 && (
            <div className="hard-gate-feedback__topics">
              <div className="hard-gate-feedback__chips">
                <span className="hard-gate-feedback__chip">Voorrang</span>
                <span className="hard-gate-feedback__chip">Gevaarherkenning</span>
                <span className="hard-gate-feedback__chip">Verkeersborden</span>
              </div>
            </div>
          )}
        </div>

        {/* ── Reassurance ── */}
        <p className="hard-gate-reassurance">
          De meeste gebruikers hebben genoeg aan <strong>1–2 weken oefenen</strong>
        </p>

        {/* ── Pricing highlight ── */}
        <div className="hard-gate-pricing">
          <div className="hard-gate-pricing__badge">Meest gekozen</div>
          <div className="hard-gate-pricing__row">
            <div>
              <div className="hard-gate-pricing__amount">€8</div>
              <div className="hard-gate-pricing__period">per week</div>
            </div>
            <ul className="hard-gate-pricing__features" aria-label="Inbegrepen">
              <li>Onbeperkt oefenexamens</li>
              <li>Focus op jouw zwakke punten</li>
              <li>Persoonlijk CBR-examen</li>
            </ul>
          </div>
        </div>

        {/* ── Primary CTA ── */}
        <button
          type="button"
          className="hard-gate-cta hard-gate-cta--primary"
          onClick={handleContinue}
        >
          Ga verder met oefenen
        </button>

        {/* ── Supporting lines ── */}
        <div className="hard-gate-support">
          <span>Stop wanneer je wilt</span>
          <span className="hard-gate-support__sep" aria-hidden>·</span>
          <span>Goedkoper dan een herexamen</span>
        </div>

        {/* ── Optional urgency ── */}
        {showUrgency && daysLeft != null && (
          <div className="hard-gate-urgency" role="status">
            Heb je examen over <strong>{daysLeft} dagen</strong>? Dan is dit het moment om door te gaan.
          </div>
        )}

      </div>
    </div>
  );
};
